import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Shield, Key, Lock, Eye, EyeOff, RefreshCw } from "lucide-react";

const AdminSecurity = () => {
  const [gate, setGate] = useState({ slug: "", pin: "" });
  const [showPin, setShowPin] = useState(false);
  const [savingGate, setSavingGate] = useState(false);

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    api.get("/admin/security/gate").then(r => setGate(r.data)).catch(() => {});
  }, []);

  const generatePin = () => {
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    setGate({ ...gate, pin });
  };

  const generateSlug = () => {
    const arr = new Uint8Array(6);
    window.crypto.getRandomValues(arr);
    const slug = Array.from(arr).map(b => b.toString(16).padStart(2, "0")).join("");
    setGate({ ...gate, slug });
  };

  const saveGate = async () => {
    if (!gate.slug || gate.slug.length < 4) { toast.error("Slug must be at least 4 characters"); return; }
    if (!/^\d{4,8}$/.test(gate.pin)) { toast.error("PIN must be 4–8 digits"); return; }
    setSavingGate(true);
    try {
      await api.post("/admin/security/gate", gate);
      toast.success("Admin gate updated. Bookmark the new URL!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingGate(false); }
  };

  const savePw = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Fill both fields"); return; }
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm_password) { toast.error("Passwords don't match"); return; }
    setSavingPw(true);
    try {
      await api.post("/admin/security/password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated. Stay signed in.");
      setPw({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingPw(false); }
  };

  return (
    <div data-testid="admin-security-page">
      <p className="eyebrow text-cream/65 mb-1">Account</p>
      <h1 className="font-hero text-3xl text-cream flex items-center gap-3 mb-2">
        <Shield size={26} /> Security
      </h1>
      <p className="text-cream/75 text-sm mb-8 max-w-xl">
        Change your admin password and the secret URL + PIN used to reach the admin login screen.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        {/* Gate slug + PIN */}
        <section className="bg-white rounded-2xl border border-border p-6" data-testid="security-gate-card">
          <div className="flex items-center gap-2 mb-3">
            <Key size={16} className="text-rose" />
            <p className="eyebrow">Admin gate URL & PIN</p>
          </div>
          <p className="text-sm text-charcoal/70 mb-5">
            The hidden entry to your dashboard. Both must be entered before the login screen appears.
          </p>

          <div className="mb-4">
            <label className="eyebrow block mb-1">Slug (the secret part of the URL)</label>
            <div className="flex gap-2">
              <Input
                value={gate.slug}
                onChange={(e) => setGate({ ...gate, slug: e.target.value.replace(/\s+/g, "").toLowerCase() })}
                placeholder="e.g. crescent-atelier"
                data-testid="gate-slug-input"
              />
              <button onClick={generateSlug} className="btn-outline px-3" title="Generate random slug" data-testid="gate-slug-randomize">
                <RefreshCw size={14} />
              </button>
            </div>
            {gate.slug && (
              <p className="text-xs text-charcoal/70 mt-1 font-mono break-all">
                URL: /atelier-entry/<strong className="text-charcoal">{gate.slug}</strong>
              </p>
            )}
          </div>

          <div className="mb-5">
            <label className="eyebrow block mb-1">PIN (4–8 digits)</label>
            <div className="flex gap-2">
              <Input
                type={showPin ? "text" : "password"}
                value={gate.pin}
                onChange={(e) => setGate({ ...gate, pin: e.target.value.replace(/[^0-9]/g, "") })}
                maxLength={8}
                placeholder="••••"
                data-testid="gate-pin-input"
              />
              <button onClick={() => setShowPin(s => !s)} className="btn-outline px-3" title="Toggle visibility" data-testid="gate-pin-show">
                {showPin ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={generatePin} className="btn-outline px-3" title="Generate random PIN" data-testid="gate-pin-randomize">
                <RefreshCw size={14} />
              </button>
            </div>
          </div>

          <button onClick={saveGate} disabled={savingGate} className="btn-primary" data-testid="save-gate">
            {savingGate ? "Saving…" : "Save gate settings"}
          </button>
          <p className="text-xs text-charcoal/70 mt-3">
            ⚠ After saving, bookmark the new URL. The old slug + PIN will stop working immediately.
          </p>
        </section>

        {/* Password change */}
        <section className="bg-white rounded-2xl border border-border p-6" data-testid="security-password-card">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={16} className="text-rose" />
            <p className="eyebrow">Admin password</p>
          </div>
          <p className="text-sm text-charcoal/70 mb-5">
            Change the password you use on the admin login screen.
          </p>

          <div className="space-y-4">
            <div>
              <label className="eyebrow block mb-1">Current password</label>
              <Input
                type="password"
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                data-testid="password-current"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1">New password (8+ chars)</label>
              <Input
                type="password"
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                data-testid="password-new"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1">Confirm new password</label>
              <Input
                type="password"
                value={pw.confirm_password}
                onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })}
                data-testid="password-confirm"
              />
            </div>
          </div>

          <button onClick={savePw} disabled={savingPw} className="btn-primary mt-5" data-testid="save-password">
            {savingPw ? "Updating…" : "Update password"}
          </button>
        </section>
      </div>
    </div>
  );
};

export default AdminSecurity;
